import { PropsWithChildren, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  AuthProvider,
  Auth0ProviderProps,
  instantiateAuth0Client
} from '@jana-technology/platform-ui'

import { useAppDispatch } from 'app/hooks'

import {
  EMPTY_LOCAL_FILTER,
  RiskUnitType
} from 'features/RiskManagement/Components/LocalFilter'
import {
  setRiskUnitSync,
  setSavedFilterSync
} from 'features/RiskManagement/Components/LocalFilter/localFilterSlice'

const domain = import.meta.env.LH_TIMP_AUTH0_DOMAIN
const clientId = import.meta.env.LH_TIMP_AUTH0_CLIENT_ID
const audience = import.meta.env.LH_TIMP_AUTH0_AUDIENCE

const Auth0ProviderWithHistory = ({ children }: PropsWithChildren) => {
  const navigate = useNavigate()
  const dispatch = useAppDispatch()

  const onRedirectCallback = useCallback<
    NonNullable<Auth0ProviderProps['onRedirectCallback']>
  >(
    (appState) => {
      if (appState) {
        // default the path here not to lose it during initial <Navigate /> redirect
        const [returnToPath = '/risk-management', returnToSearch] =
          appState.returnTo?.split('?') ?? []

        const returnToSearchParams = new URLSearchParams(returnToSearch)

        returnToSearchParams.delete('error')
        returnToSearchParams.delete('error_message')

        navigate(
          { pathname: returnToPath, search: returnToSearchParams.toString() },
          { replace: true }
        )
      }

      // TODO: [FE DEBT] clear local filter on manual login within local filter itself
      dispatch(setSavedFilterSync(EMPTY_LOCAL_FILTER))
      dispatch(setRiskUnitSync(RiskUnitType.TotalRisk))
    },
    [dispatch, navigate]
  )

  const redirectUri = globalThis.location?.origin ?? ''

  const auth0Client = instantiateAuth0Client({
    domain,
    clientId,
    authorizationParams: {
      redirect_uri: redirectUri,
      // auth0-react#101
      audience
    },
    useRefreshTokens: true,
    cacheLocation: 'localstorage',
    /**
     * In Auth0 SPA SDK v2 (that Platform UI uses) `useRefreshTokensFallback` is set to `false` by default.
     * This means that the SDK will not attempt to use the refresh token if the access token is expired.
     * This is a breaking change from v1, where the SDK would automatically use the refresh token to get a new access token.
     * To revert to the old behavior, set `useRefreshTokensFallback` to `true`.
     * https://community.auth0.com/t/auth0-spa-2-x-returning-missing-refresh-token/98999/18
     */
    useRefreshTokensFallback: true
  })

  return (
    <AuthProvider
      auth0Client={auth0Client}
      onRedirectCallback={onRedirectCallback}
    >
      {children}
    </AuthProvider>
  )
}

export default Auth0ProviderWithHistory
